import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDescriptionTasks } from "../../../store/slices/DescriptionTask";
import { fetchAllQuotations } from "../../../store/slices/generateQuotationSlice";
import { fetchHardwareDescriptions } from "../../../store/slices/hardwareDescriptionSlice";
import { fetchOtherExpenses } from "../../../store/slices/otherexpenses";
import { fetchSalesTaxes } from "../../../store/slices/salesTaxesSlice";
import { useCurrencySymbol } from "../../../utils/currencyUtils";
import CommaConvert from "../../../utils/CommaConvert";
import { fetchQuotations } from "../../../store/slices/hourlyRates";

const isInstallationTypeService = (typeService) =>
  String(typeService || "").toLowerCase().includes("installation");

/** Installation hourly rates in Settings are stored with createdBy = assigned manager's employee id. */
const resolveInstallationRatesOwnerId = (
  item,
  employees,
  allPersons,
  personManagerStatus
) => {
  const managerEmail = Array.isArray(item?.AssignedManagerEmail)
    ? item.AssignedManagerEmail[0]
    : item?.AssignedManagerEmail;

  if (managerEmail) {
    const managerEmp = employees.find(
      (emp) =>
        emp.EmpEmail &&
        emp.EmpEmail.toLowerCase() === String(managerEmail).trim().toLowerCase()
    );
    if (managerEmp?._id) return managerEmp._id;
  }

  for (const [name, personData] of allPersons) {
    if (!personManagerStatus[name]) continue;

    const managerEmp =
      employees.find(
        (emp) =>
          emp.EmpEmail &&
          personData.email &&
          emp.EmpEmail.toLowerCase() === personData.email.toLowerCase()
      ) ||
      employees.find((emp) => {
        const fullName = `${emp.EmpFirstName || ""} ${emp.EmpLastName || ""}`
          .trim()
          .toLowerCase();
        return fullName === name.trim().toLowerCase();
      });

    if (managerEmp?._id) return managerEmp._id;
    if (personData.personId) return personData.personId;
  }

  const managerQuote = (item?.quotationDetails || []).find((q) => {
    const designation = String(q?.designation || "").toLowerCase();
    return (
      designation.includes("installation") &&
      (designation.includes("manager") || designation.includes("project manager")) &&
      !designation.includes("employee")
    );
  });

  return managerQuote?.createdBy || null;
};

const SalesData = ({
  filteredQuotation,
  selectedProject,
  calculatedProjects,
  tasks,
  items,
  formatOtherExpense,
  formatTotalExpense,
  useProjectAssignedRates = false,
}) => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const formatOtherExpenseValue = formatOtherExpense || USAComma;
  const formatTotalExpenseValue = formatTotalExpense || USAComma;
  const formatDecimalAmount = useMemo(() => {
    const countryCode = String(localStorage.getItem("countrycode") || "").trim();
    const region = String(localStorage.getItem("region") || "")
      .trim()
      .toUpperCase();
    const isInLocale =
      countryCode === "+91" ||
      countryCode === "91" ||
      region === "IN" ||
      region === "INDIA";
    const locale = isInLocale ? "en-IN" : "en-US";
    return (num) => {
      if (num === undefined || num === null) return "";
      const value = Number(num);
      if (isNaN(value)) return "";
      return value.toLocaleString(locale, {
        maximumFractionDigits: 10,
        minimumFractionDigits: 0,
      });
    };
  }, []);
  const formatHours = (value) =>
    new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(Number(value) || 0);
  const { employees } = useSelector((state) => state.employees);
  const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
  const { quotations: hourlyRateQuotations } = useSelector((state) => state.hourlyRateQuotation);
  const [perdiem, setperdiem] = useState(0);
  const [averagecost, setaveragecost] = useState(0);
  const [totalServiceGrandTotal, setTotalServiceGrandTotal] = useState(0);
  const tableContainerRef = useRef(null);

  console.log("filteredQuotationfilteredQuotation", filteredQuotation);
  useEffect(() => {
    dispatch(fetchDescriptionTasks());
    dispatch(fetchAllQuotations());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchOtherExpenses());
    dispatch(fetchSalesTaxes());
    dispatch(fetchQuotations()); // Fetch hourly rate quotations for accurate matching
  }, [dispatch]);

  useEffect(() => {
    if (useProjectAssignedRates) return;

    if (otherExpenses && otherExpenses.length > 0) {
      const perdiemExpense = otherExpenses.find(
        (expense) =>
          expense.description && expense.description.toLowerCase() === "perdiem"
      );

      const averageCostExpense = otherExpenses.find(
        (expense) =>
          expense.description &&
          expense.description.toLowerCase() === "average cost"
      );

      setperdiem(perdiemExpense ? perdiemExpense.cost || 0 : 0);
      setaveragecost(averageCostExpense ? averageCostExpense.cost || 0 : 0);
    } else {
      setperdiem(0);
      setaveragecost(0);
    }
  }, [otherExpenses, useProjectAssignedRates]);

  const getDescriptionValues = useCallback(
    (projectID, personID, typeService) => {
      if (!tasks) return [];
      return tasks.filter(
        (desc) => desc.projectid === projectID && desc.taskownerid === personID
      );
    },
    [tasks]
  );

  const getTotalDescriptionValue = useCallback(
    (projectID, personID, typeService, installRateOverrides) => {
      const descriptions = getDescriptionValues(
        projectID,
        personID,
        typeService
      );

      let totalValue = descriptions.reduce(
        (sum, desc) => sum + (desc.expenses || 0),
        0
      );

      if (typeService && typeService.toLowerCase().includes("installation")) {
        let maxTripDuration = 0;
        const uniqueTripDurations = new Set();

        descriptions.forEach((desc) => {
          if (desc.tripduration) {
            uniqueTripDurations.add(desc.tripduration);
            maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
          }
        });

        if (maxTripDuration > 0) {
          const installPerdiem =
            installRateOverrides?.perdiem ?? perdiem;
          const installAvgcost =
            installRateOverrides?.avgcost ?? averagecost;
          totalValue +=
            maxTripDuration * installPerdiem +
            maxTripDuration * installAvgcost;
        }
      }

      return totalValue;
    },
    [getDescriptionValues, perdiem, averagecost]
  );

  const getHardwareValues = useCallback(
    (projectID, typeService) => {
      if (!items) return [];
      return items.filter(
        (desc) =>
          desc.projectid === projectID && desc.typeservice === typeService
      );
    },
    [items]
  );

  const getTotalHardwareCostValue = useCallback(
    (projectID, personID, typeService) => {
      let hardwareValue = 0;
      const hardwareCost = getHardwareValues(projectID, typeService);
      const hardwareByDescription = {};

      hardwareCost.forEach((hw) => {
        const description = hw.assemblepartdescription;

        if (!hardwareByDescription[description]) {
          hardwareByDescription[description] = hw;
        } else {
          if (hw.totalprice > hardwareByDescription[description].totalprice) {
            hardwareByDescription[description] = hw;
          }
        }
      });

      Object.values(hardwareByDescription).forEach((hw) => {
        if (hw.totalprice) {
          hardwareValue += hw.totalprice;
        }
      });

      return hardwareValue;
    },
    [getHardwareValues]
  );

  // const transformFilteredQuotation = useCallback(
  //   (filteredQuotation, employees) => {
  //     const result = [];
  //     Object.entries(filteredQuotation || {}).forEach(
  //       ([projectName, typeServices]) => {
  //         Object.entries(typeServices).forEach(([typeService, items]) => {
  //           items.forEach((item) => {
  //             if (!item?.WorkEntries?.length) return;

  //             // Group work entries by person
  //             const groupedByPerson = {};
  //             const personRoleMap = {};

  //             item.WorkEntries.forEach((entry) => {
  //               const personName = entry?.createdBy?.name;
  //               const personRoles = entry?.createdBy?.role || [];
  //               const personId = entry?.taskownerid;

  //               if (!groupedByPerson[personName]) {
  //                 groupedByPerson[personName] = { totalHours: 0, personId };
  //               }
  //               groupedByPerson[personName].totalHours += entry.hoursspent || 0;

  //               if (!personRoleMap[personName]) {
  //                 personRoleMap[personName] = { roles: personRoles, personId };
  //               }
  //             });

  //             // Sort persons: Project Manager first, then Operations
  //             const sortedPersons = Object.entries(groupedByPerson).sort(([nameA], [nameB]) => {
  //               const rolesA = personRoleMap[nameA]?.roles || [];
  //               const rolesB = personRoleMap[nameB]?.roles || [];

  //               const hasManagerA = rolesA.some(role =>
  //                 role.toLowerCase().includes("manager")
  //               );
  //               const hasManagerB = rolesB.some(role =>
  //                 role.toLowerCase().includes("manager")
  //               );

  //               if (hasManagerA && !hasManagerB) return -1;
  //               if (!hasManagerA && hasManagerB) return 1;
  //               return 0;
  //             });

  //             const isHardwareService = item?.TypeService?.toLowerCase().includes("hardware");

  //             // Check if there's a Project Manager for this hardware service
  //             let hasProjectManager = false;
  //             if (isHardwareService) {
  //               hasProjectManager = sortedPersons.some(([name]) => {
  //                 const roles = personRoleMap[name]?.roles || [];
  //                 return roles.some(role =>
  //                   role.toLowerCase().includes("manager")
  //                 );
  //               });
  //             }

  //             // Track if hardware cost has been assigned for this hardware service
  //             let hardwareCostAssigned = false;

  //             // Convert grouped persons into array with costs
  //             sortedPersons.forEach(([name, personData], index) => {
  //               const totalHours = personData.totalHours;
  //               const personId = personData.personId;
  //               const roles = personRoleMap[name]?.roles || [];
  //               const ProjectID = item?.ProjectID;
  //               const ProjectNotes = item?.ProjectNotes;
  //               const ServiceQuotedValue = item?.QuotedValue;

  //               // Find quotation details that match the taskownerid
  //               const matchedQuotation = item?.quotationDetails?.find(
  //                 (q) => q.personid === personId
  //               );

  //               // If no direct personid match, fall back to role matching
  //               const finalMatchedQuotation =
  //                 matchedQuotation ||
  //                 item?.quotationDetails?.find((q) =>
  //                   roles.some((role) =>
  //                     q.designation?.toLowerCase().includes(role?.toLowerCase())
  //                   )
  //                 );

  //               const currentEmployee = employees.find((emp) => {
  //                 const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
  //                   .trim()
  //                   .toLowerCase();
  //                 return fullName === name.trim().toLowerCase();
  //               });

  //               const capitalizedRoles =
  //                 currentEmployee?.customRoles?.map(
  //                   (role) =>
  //                     role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
  //                 ) || [];

  //               const hourlyRate =
  //                 (finalMatchedQuotation?.hourlysalaray || 0) +
  //                 (finalMatchedQuotation?.overheadpercentage || 0);

  //               const totalCost = totalHours * hourlyRate;

  //               const descriptionTotal = getTotalDescriptionValue(
  //                 item.ProjectID,
  //                 personId,
  //                 item.TypeService
  //               );

  //               const hardwareTotal = getTotalHardwareCostValue(
  //                 item.ProjectID,
  //                 personId,
  //                 item.TypeService
  //               );

  //               const isOperationsRole = capitalizedRoles.includes("Operations");
  //               const isProjectManagerRole = capitalizedRoles.includes("Project manager");

  //               // For hardware services without project manager, assign hardware cost to first operations employee
  //               let shouldGetHardwareCost = false;

  //               if (isHardwareService && !hasProjectManager) {
  //                 // Check if this is the first operations employee and hardware cost hasn't been assigned yet
  //                 if (isOperationsRole && !hardwareCostAssigned) {
  //                   shouldGetHardwareCost = true;
  //                   hardwareCostAssigned = true;
  //                 }
  //               }

  //               // Calculate grandTotal based on role and service type
  //               let grandTotal = 0;

  //               if (isHardwareService) {
  //                 // For Hardware service type:
  //                 if (isOperationsRole) {
  //                   if (shouldGetHardwareCost) {
  //                     // First Operations employee gets hardwareTotal + descriptionTotal
  //                     grandTotal = hardwareTotal + descriptionTotal;
  //                   } else {
  //                     // Other Operations employees get descriptionTotal only
  //                     grandTotal = descriptionTotal;
  //                   }
  //                 } else if (isProjectManagerRole) {
  //                   // Project Manager in Hardware: hardwareTotal + descriptionTotal
  //                   grandTotal = hardwareTotal + descriptionTotal;
  //                 } else {
  //                   // Other roles in Hardware: descriptionTotal + totalCost
  //                   grandTotal = descriptionTotal + totalCost;
  //                 }
  //               } else {
  //                 // For non-Hardware service types:
  //                 // All roles (including Operations) get descriptionTotal + totalCost
  //                 grandTotal = descriptionTotal + totalCost;
  //               }

  //               result.push({
  //                 ServiceQuotedValue,
  //                 projectName,
  //                 typeService: item.TypeService,
  //                 resourceType: item.ResourceType,
  //                 personId,
  //                 ProjectID,
  //                 ProjectNotes,
  //                 personName: name,
  //                 roles: capitalizedRoles,
  //                 totalHours,
  //                 hourlyRate,
  //                 totalCost,
  //                 descriptionTotal,
  //                 hardwareTotal,
  //                 grandTotal,
  //                 isOperationsRole,
  //                 isProjectManagerRole,
  //                 isHardwareService,
  //                 shouldGetHardwareCost,
  //                 hasProjectManagerInService: hasProjectManager,
  //               });
  //             });
  //           });
  //         });
  //       }
  //     );

  //     return result;
  //   },
  //   [getTotalDescriptionValue, getTotalHardwareCostValue]
  // );
  const transformFilteredQuotation = useCallback(
    (filteredQuotation, employees) => {
      const result = [];
      Object.entries(filteredQuotation || {}).forEach(
        ([projectName, typeServices]) => {
          Object.entries(typeServices).forEach(([typeService, items]) => {
            items.forEach((item) => {
              // Create a map to track all persons (from both WorkEntries and Assignedperson)
              const allPersonsMap = new Map();

              // Track if we should process even without WorkEntries (for managers)
              const shouldProcess =
                item?.WorkEntries?.length || item?.Assignedperson?.length;

              if (!shouldProcess) return;

              // Helper function to get employee by email
              const getEmployeeByEmail = (email) => {
                return employees.find(
                  (emp) =>
                    emp.EmpEmail &&
                    emp.EmpEmail.toLowerCase() === email?.toLowerCase()
                );
              };

              // Helper function to get employee by name (fallback)
              const getEmployeeByName = (name) => {
                return employees.find((emp) => {
                  const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`
                    .trim()
                    .toLowerCase();
                  return fullName === name.trim().toLowerCase();
                });
              };

              // First, add persons from WorkEntries
              if (item?.WorkEntries?.length) {
                item.WorkEntries.forEach((entry) => {
                  const personName = entry?.createdBy?.name;
                  const personRoles = entry?.createdBy?.role || [];
                  const personId = entry?.taskownerid;

                  if (personName && personId) {
                    if (!allPersonsMap.has(personName)) {
                      allPersonsMap.set(personName, {
                        personId,
                        totalHours: entry.hoursspent || 0,
                        roles: personRoles,
                        fromWorkEntries: true,
                        fromAssignedPerson: false,
                        email: entry?.createdBy?.email, // Store email if available
                      });
                    } else {
                      // Update hours if person already exists
                      const existing = allPersonsMap.get(personName);
                      existing.totalHours += entry.hoursspent || 0;
                    }
                  }
                });
              }

              // Then, add persons from Assignedperson who weren't in WorkEntries
              if (item?.Assignedperson?.length) {
                item.Assignedperson.forEach((assignedPerson) => {
                  const personName = assignedPerson.name;
                  const personEmail = assignedPerson.email;

                  if (personName && personEmail) {
                    // Find employee by email first (more reliable)
                    const employeeByEmail = getEmployeeByEmail(personEmail);
                    const employeeByName = getEmployeeByName(personName);

                    // Use employee found by email, or by name as fallback
                    const currentEmployee = employeeByEmail || employeeByName;

                    if (currentEmployee) {
                      // Use the employee's _id from employees data
                      const personId =
                        currentEmployee._id || currentEmployee.id;

                      if (!allPersonsMap.has(personName)) {
                        allPersonsMap.set(personName, {
                          personId,
                          totalHours: 0, // No hours from WorkEntries
                          roles: currentEmployee?.customRoles || [],
                          fromWorkEntries: false,
                          fromAssignedPerson: true,
                          email: personEmail,
                        });
                      }
                    } else {
                      console.warn(
                        `Employee not found for Assignedperson: ${personName} (${personEmail})`
                      );
                    }
                  }
                });
              }

              // Convert Map to array
              const allPersons = Array.from(allPersonsMap.entries());

              if (allPersons.length === 0) return;

              // Determine manager status for each person
              const personManagerStatus = {};

              allPersons.forEach(([name, personData]) => {
                // Check if employee has manager role
                const isManager = personData.roles.some((role) =>
                  role.toLowerCase().includes("manager")
                );
                personManagerStatus[name] = isManager;
              });

              // Sort persons: Project Manager first, then Operations
              const sortedPersons = allPersons.sort(([nameA], [nameB]) => {
                const isManagerA = personManagerStatus[nameA];
                const isManagerB = personManagerStatus[nameB];

                if (isManagerA && !isManagerB) return -1;
                if (!isManagerA && isManagerB) return 1;

                // If both have same manager status, sort by name or maintain order
                return 0;
              });

              const isHardwareService =
                item?.TypeService?.toLowerCase().includes("hardware");

              // Check if there's a Project Manager for this hardware service
              let hasProjectManager = false;
              if (isHardwareService) {
                hasProjectManager = sortedPersons.some(([name]) => {
                  return personManagerStatus[name];
                });
              }

              // Track if hardware cost has been assigned for this hardware service
              let hardwareCostAssigned = false;

              // Track which quotations have been used to ensure each employee gets their own unique quotation
              const usedQuotationIds = new Set();

              const isInstallationService = isInstallationTypeService(item?.TypeService);
              const installationRatesOwnerId = isInstallationService
                ? resolveInstallationRatesOwnerId(
                    item,
                    employees,
                    allPersons,
                    personManagerStatus
                  )
                : null;
              const matchesInstallationRatesScope = (q) => {
                if (!isInstallationService || !installationRatesOwnerId) return true;
                return (
                  String(q?.createdBy || "") === String(installationRatesOwnerId)
                );
              };

              // DEBUG: Log to see if we're getting correct personIds
              console.log("Processing service:", {
                projectName,
                typeService,
                isHardwareService,
                allPersons: allPersons.map(([name, data]) => ({
                  name,
                  personId: data.personId,
                  email: data.email,
                  isManager: personManagerStatus[name],
                })),
              });

              // Convert grouped persons into array with costs
              sortedPersons.forEach(([name, personData], index) => {
                const totalHours = personData.totalHours;
                const personId = personData.personId;
                const roles = personData.roles;
                const ProjectID = item?.ProjectID;
                const ProjectNotes = item?.ProjectNotes;
                const ServiceQuotedValue = item?.QuotedValue;

                // Find employee data to ensure we have the right person
                const currentEmployee =
                  getEmployeeByEmail(personData.email) ||
                  getEmployeeByName(name);

                // DEBUG: Check if we found the employee
                console.log("Person details:", {
                  name,
                  personIdFromData: personId,
                  employeeFound: !!currentEmployee,
                  employeeId: currentEmployee?._id,
                  email: personData.email,
                });

                // Use employee's ID if we found one (more reliable)
                const finalPersonId = currentEmployee?._id || personId;

                // CRITICAL FIX: First try to match from hourlyRateQuotations (from Settings) by personid
                // This ensures we get the correct rate for each employee from the hourly rates settings
                let matchedQuotation = null;
                
                // Helper: unique key for a quotation record (never include current employee) so reuse is tracked correctly
                const getQuotationRecordId = (q) => q._id || `${q.personid ?? "node"}_${q.designation ?? ""}`;

                // Try to find quotation from hourlyRateQuotations first (Settings → Hourly Rates & Expenses) by employee ID
                if (
                  !useProjectAssignedRates &&
                  hourlyRateQuotations &&
                  hourlyRateQuotations.length > 0 &&
                  finalPersonId
                ) {
                  const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
                  matchedQuotation = hourlyRateQuotations.find((q) => {
                    const qDesignation = q.designation?.toLowerCase() || "";
                    return (
                      q.personid === finalPersonId &&
                      qDesignation.includes(serviceTypeMatch) &&
                      matchesInstallationRatesScope(q) &&
                      !usedQuotationIds.has(getQuotationRecordId(q))
                    );
                  });
                }

                // If no match from Settings, try quotationDetails from the item (by personid)
                if (!matchedQuotation) {
                  matchedQuotation = item?.quotationDetails?.find(
                    (q) =>
                      (String(q.personid) === String(finalPersonId) ||
                        q.personid === finalPersonId) &&
                      (!useProjectAssignedRates ||
                        matchesInstallationRatesScope(q)) &&
                      !usedQuotationIds.has(getQuotationRecordId(q))
                  );
                }

                // If no direct personid match, fall back to role-based matching
                // CRITICAL: Match managers to manager rates (527) and employees to employee rates (105)
                // CRITICAL: Ensure each employee gets their own unique quotation
                let finalMatchedQuotation = matchedQuotation;
                
                if (!finalMatchedQuotation && (!useProjectAssignedRates || isInstallationService)) {
                  // Determine if this person is a manager or employee
                  const isManager = roles.some((role) =>
                    role.toLowerCase().includes("manager") || role.toLowerCase().includes("project manager")
                  );
                  const isOperations = roles.some((role) =>
                    role.toLowerCase().includes("operations") || role.toLowerCase().includes("employee")
                  );
                  
                  if (isManager) {
                    const managerQuotes = item?.quotationDetails?.filter((q) => {
                      const qDesignation = q.designation?.toLowerCase() || "";
                      return (
                        !usedQuotationIds.has(getQuotationRecordId(q)) &&
                        matchesInstallationRatesScope(q) &&
                        (qDesignation.includes("project manager") ||
                         (qDesignation.includes("manager") && !qDesignation.includes("operations"))) &&
                        !qDesignation.includes("employee")
                      );
                    });
                    finalMatchedQuotation = managerQuotes?.find((q) =>
                      q.designation?.toLowerCase().includes("project manager")
                    ) || managerQuotes?.[0];
                  } else if (isOperations) {
                    // Match by employee ID first from Settings (Hourly Rates & Expenses); only use designation fallback once per record
                    let employeeQuotes = [];
                    if (
                      !useProjectAssignedRates &&
                      hourlyRateQuotations &&
                      hourlyRateQuotations.length > 0
                    ) {
                      const serviceTypeMatch = item?.TypeService?.toLowerCase() || "";
                      employeeQuotes = hourlyRateQuotations.filter((q) => {
                        const qDesignation = q.designation?.toLowerCase() || "";
                        return (
                          !usedQuotationIds.has(getQuotationRecordId(q)) &&
                          matchesInstallationRatesScope(q) &&
                          qDesignation.includes(serviceTypeMatch) &&
                          !qDesignation.includes("manager") &&
                          !qDesignation.includes("project manager") &&
                          (qDesignation.includes("operations") || qDesignation.includes("employee"))
                        );
                      });
                    }
                    if (employeeQuotes.length === 0) {
                      employeeQuotes = item?.quotationDetails?.filter((q) => {
                        const qDesignation = q.designation?.toLowerCase() || "";
                        return (
                          !usedQuotationIds.has(getQuotationRecordId(q)) &&
                          matchesInstallationRatesScope(q) &&
                          !qDesignation.includes("manager") &&
                          !qDesignation.includes("project manager") &&
                          (qDesignation.includes("operations") || qDesignation.includes("employee"))
                        );
                      }) || [];
                    }
                    // Prefer quotation tied to this employee (personid); otherwise first unused by record id
                    const quoteByPersonId = employeeQuotes?.find((q) =>
                      q.personid && q.personid === finalPersonId && !usedQuotationIds.has(getQuotationRecordId(q))
                    );
                    if (quoteByPersonId) {
                      finalMatchedQuotation = quoteByPersonId;
                    } else if (
                      !isInstallationService &&
                      employeeQuotes?.length > 0
                    ) {
                      finalMatchedQuotation = employeeQuotes.find((q) => !usedQuotationIds.has(getQuotationRecordId(q)));
                    }
                  } else {
                    finalMatchedQuotation = item?.quotationDetails?.find((q) =>
                      !usedQuotationIds.has(getQuotationRecordId(q)) &&
                      matchesInstallationRatesScope(q) &&
                      roles.some((role) =>
                        q.designation?.toLowerCase().includes(role?.toLowerCase())
                      )
                    );
                  }
                }

                // Mark this quotation as used (quotation record identity only — no employee id) so the same record is not reused
                if (finalMatchedQuotation) {
                  usedQuotationIds.add(getQuotationRecordId(finalMatchedQuotation));
                }

                const capitalizedRoles = roles.map(
                  (role) =>
                    role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                );

                const hourlyRate =
                  (finalMatchedQuotation?.hourlysalaray || 0) +
                  (finalMatchedQuotation?.overheadpercentage || 0);

                const totalCost = totalHours * hourlyRate;

                // Get description total using the final personId
                const descriptionTotal = getTotalDescriptionValue(
                  item.ProjectID,
                  finalPersonId,
                  item.TypeService,
                  useProjectAssignedRates
                    ? {
                        perdiem: item.projectPerdiem,
                        avgcost: item.projectAvgcost,
                      }
                    : undefined
                );

                // DEBUG: Check description total
                console.log(`Description total for ${name}:`, {
                  projectID: item.ProjectID,
                  personId: finalPersonId,
                  typeService: item.TypeService,
                  descriptionTotal,
                });

                const hardwareTotal = getTotalHardwareCostValue(
                  item.ProjectID,
                  finalPersonId,
                  item.TypeService
                );

                const isOperationsRole =
                  capitalizedRoles.includes("Operations");
                const isProjectManagerRole = personManagerStatus[name];

                // For hardware services without project manager, assign hardware cost to first operations employee
                let shouldGetHardwareCost = false;

                if (isHardwareService && !hasProjectManager) {
                  // Check if this is the first operations employee and hardware cost hasn't been assigned yet
                  if (isOperationsRole && !hardwareCostAssigned) {
                    shouldGetHardwareCost = true;
                    hardwareCostAssigned = true;
                  }
                }

                // Calculate grandTotal based on role and service type
                let grandTotal = 0;

                if (isHardwareService) {
                  // DEBUG: Log hardware calculation
                  console.log(`Hardware calculation for ${name}:`, {
                    isOperationsRole,
                    shouldGetHardwareCost,
                    isProjectManagerRole,
                    hasProjectManager,
                    hardwareTotal,
                    descriptionTotal,
                    totalCost,
                  });

                  // For Hardware service type:
                  if (isOperationsRole) {
                    if (shouldGetHardwareCost) {
                      // First Operations employee gets hardwareTotal + descriptionTotal
                      grandTotal = hardwareTotal + descriptionTotal;
                    } else {
                      // Other Operations employees get descriptionTotal only
                      grandTotal = descriptionTotal;
                    }
                  } else if (isProjectManagerRole) {
                    // Project Manager in Hardware: hardwareTotal + descriptionTotal
                    grandTotal = hardwareTotal + descriptionTotal;
                  } else {
                    // Other roles in Hardware: descriptionTotal + totalCost
                    grandTotal = descriptionTotal + totalCost;
                  }
                } else {
                  // For non-Hardware service types:
                  // All roles (including Operations) get descriptionTotal + totalCost
                  grandTotal = descriptionTotal + totalCost;
                }

                result.push({
                  ServiceQuotedValue,
                  projectName,
                  typeService: item.TypeService,
                  resourceType: item.ResourceType,
                  personId: finalPersonId,
                  ProjectID,
                  ProjectNotes,
                  personName: name,
                  roles: capitalizedRoles,
                  totalHours,
                  hourlyRate,
                  totalCost,
                  descriptionTotal,
                  hardwareTotal,
                  grandTotal,
                  isOperationsRole,
                  isProjectManagerRole,
                  isHardwareService,
                  shouldGetHardwareCost,
                  hasProjectManagerInService: hasProjectManager,
                  fromWorkEntries: personData.fromWorkEntries,
                  fromAssignedPerson: personData.fromAssignedPerson,
                  email: personData.email, // Store email for reference
                });

                // DEBUG: Log final result
                console.log(`Final result for ${name}:`, {
                  descriptionTotal,
                  hardwareTotal,
                  grandTotal,
                  isProjectManagerRole,
                  finalPersonId,
                });
              });
            });
          });
        }
      );

      return result;
    },
    [
      getTotalDescriptionValue,
      getTotalHardwareCostValue,
      hourlyRateQuotations,
      useProjectAssignedRates,
    ]
  );

  const groupByTypeService = (processedData) => {
    return processedData.reduce((acc, curr) => {
      if (!acc[curr.typeService]) {
        acc[curr.typeService] = [];
      }
      acc[curr.typeService].push(curr);
      return acc;
    }, {});
  };

  const processedData = useMemo(() => {
    return transformFilteredQuotation(filteredQuotation, employees);
  }, [filteredQuotation, employees, transformFilteredQuotation]);

  const groupedData = useMemo(() => {
    return groupByTypeService(processedData);
  }, [processedData]);

  console.log("groupedData", groupedData);

  useEffect(() => {
    if (groupedData && Object.keys(groupedData).length > 0) {
      let sum = 0;
      Object.values(groupedData).forEach((rows) => {
        const serviceGrandTotal = rows.reduce(
          (acc, row) => acc + (Number(row.grandTotal) || 0),
          0
        );
        sum += serviceGrandTotal;
      });
      setTotalServiceGrandTotal(sum);
    }
  }, [groupedData]);

  return (
    <div className="pt-3">
      <div
        ref={tableContainerRef}
        style={{ scrollBehavior: "smooth" }}
        className="maintable w-full overflow-auto"
      >
        <div className="w-full flex text-xs font-medium">
          <div className="w-[16.6%] first-line:flex flex justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8] ">
            Resources Designation
          </div>
          <div className="w-[16.6%] flex  justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8] ">
            Rate Per Hour ({currency})
          </div>
          <div className=" w-[16.6%] flex  justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8] ">
            Efforts In Hours
          </div>
          <div className="w-[16.6%] flex  justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8] ">
            Total Efforts ({currency})
          </div>
          <div className="w-[16.6%] flex flex-col  justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8]">
            Other Expenses ({currency})
          </div>
          <div className=" w-[16.6%] flex  justify-center  align-middle items-center p-4  text-center bg-[#e8e8e8]">
            Total Expenses ({currency})
          </div>
        </div>
        <div className="Employee_Efforts_Calculation w-full">
          {Object.entries(groupedData)
            .sort(([a], [b]) => {
              if (a === "Hardware") return 1;
              if (b === "Hardware") return -1;
              return 0;
            })
            .map(([typeService, rows]) => {
              let operationsCounter = 0;
              let projectManagerCounter = 0;

              return (
                <div
                  key={typeService}
                  className="flex text-xs font-medium border-b border-black w-full"
                >
                  <div className=" w-full">
                    {rows.map((row, idx) => {
                      let displayText = "";

                      if (row.isOperationsRole) {
                        operationsCounter++;
                        displayText = `${typeService} Employee ${operationsCounter}`;
                      } else if (row.isProjectManagerRole) {
                        projectManagerCounter++;
                        displayText = `${typeService} Manager ${projectManagerCounter}`;
                      } else {
                        const rawRole = String(row.roles[0] || "Employee").trim();
                        const roleDisplay = rawRole.replace(
                          /\bproject\s+manager\b/gi,
                          "Manager"
                        );
                        displayText = `${typeService} ${roleDisplay}`;
                      }

                      // Determine if we should hide rate and hours columns
                      // Hide for Operations ONLY when it's Hardware service
                      const hideRateAndHours =
                        row.isOperationsRole && row.isHardwareService;

                      return (
                        <div key={idx} className="flex w-full">
                          <div className="w-[16.6%] p-1 capitalize flex justify-center items-center text-center border-b">
                            {displayText}
                          </div>
                          <div className="w-[16.6%] flex justify-center items-center text-center border-b">
                            {hideRateAndHours || typeService === "Hardware"
                              ? ""
                              : formatDecimalAmount(row.hourlyRate)}
                          </div>
                          <div className="w-[16.6%] p-1   flex justify-center items-center text-center border-b">
                            {hideRateAndHours || typeService === "Hardware"
                              ? ""
                              : formatHours(row.totalHours)}
                          </div>

                          <div className="w-[16.6%] p-1  flex justify-center items-center text-center border-b">
                            {row.isOperationsRole &&
                            row.isHardwareService &&
                            !row.shouldGetHardwareCost
                              ? "" // Empty for Operations in Hardware service (except first one if no PM)
                              : typeService === "Hardware"
                              ? formatDecimalAmount(row.hardwareTotal)
                              : formatDecimalAmount(row.totalCost)}
                          </div>
                          <div className="w-[16.6%] p-1 = flex justify-center items-center text-center border-b">
                            {formatOtherExpenseValue(row.descriptionTotal)}
                          </div>
                          <div className="w-[16.6%] p-1 flex justify-center items-center text-center border-b">
                            {formatTotalExpense
                              ? formatOtherExpenseValue(row.grandTotal)
                              : USAComma(row.grandTotal)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>

        <div className="Totaltable flex w-full text-center text-sm font-semibold">
          <div className="w-[16.6%] border-b  p-1 text-center   ">
            {/* Quoted Price */}
          </div>

          <div className="w-[16.6%] border-b k p-1 text-center  "></div>
          <div className="w-[16.6%] border-b  p-1 text-center  "></div>

          <div className="w-[16.6%] border-b  p-1 text-center "></div>
          <div className=" w-[16.6%] descriptionvaluetotal border-b  flex-shrink-0 p-1 text-center"></div>
          <div className="allgrandtotal w-[16.6%] alltotal  p-1 border-b  text-center  ">
            {formatTotalExpenseValue(totalServiceGrandTotal)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesData;
